/**
 * Minimal synchronous pub/sub. Every system talks through this instead of
 * holding references to each other, so new systems can be dropped in without
 * touching the ones that already exist.
 */
export class EventBus {
  constructor() {
    this._map = new Map();
  }

  on(type, fn) {
    let set = this._map.get(type);
    if (!set) { set = new Set(); this._map.set(type, set); }
    set.add(fn);
    return () => this.off(type, fn);
  }

  once(type, fn) {
    const off = this.on(type, (payload) => { off(); fn(payload); });
    return off;
  }

  off(type, fn) {
    const set = this._map.get(type);
    if (set) set.delete(fn);
  }

  emit(type, payload) {
    const set = this._map.get(type);
    if (!set) return;
    // Copy so handlers may unsubscribe during dispatch.
    for (const fn of [...set]) {
      try { fn(payload, type); }
      catch (err) { console.error(`[bus] handler for "${type}" threw:`, err); }
    }
  }

  clear() { this._map.clear(); }
}

/** Event name constants. Keeps typos loud instead of silent. */
export const EV = {
  BOOT_PROGRESS:   'boot:progress',
  LEVEL_LOADED:    'level:loaded',
  LEVEL_START:     'level:start',
  LEVEL_COMPLETE:  'level:complete',
  LEVEL_FAILED:    'level:failed',
  PLAYER_SPAWN:    'player:spawn',
  PLAYER_DIED:     'player:died',
  PLAYER_LANDED:   'player:landed',
  PLAYER_JUMPED:   'player:jumped',
  PLAYER_TELEPORT: 'player:teleport',
  PLAYER_BOOST:    'player:boost',
  PORTAL_PLACED:   'portal:placed',
  SHEAR:           'beam:shear',
  PORTAL_DENIED:   'portal:denied',
  PORTAL_CLEARED:  'portal:cleared',
  PICKUP:          'gameplay:pickup',
  SHATTER:         'gameplay:shatter',
  CHECKPOINT:      'gameplay:checkpoint',
  OBJECTIVE:       'gameplay:objective',
  TOAST:           'ui:toast',
  SETTINGS_CHANGED:'settings:changed',
};
